import Image from 'next/image';

import { Box, Divider, Flex, Heading, LinkBox, LinkOverlay, List, ListItem } from '@chakra-ui/react';
import { Difficulty1Icon, Difficulty2Icon, Difficulty3Icon, TimeIcon } from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { useCourseLink } from '~/features/program/hooks/use-course-link';
import { useI18n } from '~/hooks/use-i18n';

export type StartForFreeSectionProps = {
  featuredFreeProgram: CatalogItem;
};

export function StartForFreeSection({ featuredFreeProgram }: StartForFreeSectionProps) {
  return (
    <Box
      as='section'
      bgColor='#F7F6F1'
      padding={{
        base: '32px 0px',
        md: '40px 0px',
      }}
    >
      <ContentContainer
        display='flex'
        alignItems={{
          md: 'center',
        }}
        flexDirection={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 4,
          md: 8,
        }}
      >
        <Box
          flex={{
            md: '0 1 450px',
          }}
        >
          <Heading
            as='h2'
            size={{
              base: 'marketing-xl',
              md: 'marketing-5xl',
            }}
            sx={{
              background: 'linear-gradient(90deg, #0B0B0B 0%, #0077D0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            textAlign={{
              base: 'center',
              md: 'start',
            }}
          >
            Get started with Azure AI for FREE
          </Heading>
        </Box>
        <Box
          flex={{
            md: '1 1 638px',
          }}
        >
          {featuredFreeProgram && <FeaturedProgramCard {...featuredFreeProgram} programKey={featuredFreeProgram.key} />}
        </Box>
      </ContentContainer>
    </Box>
  );
}

function FeaturedProgramCard({
  title,
  image,
  difficultyLevel,
  duration,
  slug,
  programKey,
}: CatalogItem & { programKey: string }) {
  const { isRtl } = useI18n();
  const courseLink = useCourseLink({ courseSlug: slug });
  return (
    <Card
      as={LinkBox}
      border='none'
      borderRadius='8px'
      color='black'
      display='flex'
      overflow='hidden'
      position='relative'
      flexDirection={{
        base: 'column',
        md: 'row',
      }}
      onClick={() =>
        trackEvent({
          type: 'click',
          name: 'Microsoft AI Skills Fest CTA Clicked',
          label: 'program_card_clicked',
          value: title ?? undefined,
          additionalAttributes: {
            product_ids: [programKey],
          },
        })
      }
    >
      <Box
        overflow='hidden'
        position='relative'
        flex={{
          base: '0 0 136px',
          md: '0 1 315px',
        }}
        sx={{
          '&::after': {
            content: '""',
            background: {
              base: 'linear-gradient(180deg, rgba(2,0,36,0) 50%, rgba(255,255,255,1) 100%)',
              md: `linear-gradient(${isRtl ? '-90deg' : '90deg'}, rgba(2,0,36,0) 50%, rgba(255,255,255,1) 100%)`,
            },
            height: '100%',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
          },
        }}
      >
        {!!image && (
          <Image alt='' loading='eager' src={image} style={{ objectFit: 'cover', pointerEvents: 'none' }} fill />
        )}
      </Box>
      <Flex
        flexDirection='column'
        gap={2}
        justifyContent='center'
        minHeight={{ md: '176px' }}
        p={4}
        flex={{
          md: '1 2 315px',
        }}
      >
        <Heading
          as={LinkOverlay}
          color='black'
          fontWeight='400'
          href={courseLink}
          size={{
            base: 'sm',
            md: 'marketing-xl',
          }}
        >
          {title}
        </Heading>
        <Divider as='div' borderColor='gray.100' />
        <List color='gray.600' display='flex' fontWeight='500' gap={2}>
          <ListItem alignItems='center' display='flex' fontSize='xs' gap={1}>
            <Box>
              {(difficultyLevel === 'Beginner' || difficultyLevel === 'Discovery' || difficultyLevel === 'Fluency') && (
                <Difficulty1Icon />
              )}
              {difficultyLevel === 'Intermediate' && <Difficulty2Icon />}
              {difficultyLevel === 'Advanced' && <Difficulty3Icon />}
            </Box>
            {difficultyLevel}
          </ListItem>
          <ListItem alignItems='center' display='flex' fontSize='xs' gap={1}>
            <Box>
              <TimeIcon />
            </Box>
            {duration}
          </ListItem>
        </List>
      </Flex>
    </Card>
  );
}
